import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, Spin, Alert, Descriptions, List, Typography, Empty } from 'antd';
import moment from 'moment';

const { Text } = Typography;

// Helper to format date
const formatDate = (dateString) => {
    return dateString ? moment(dateString).format('L') : 'N/A';
};

const ReadOnlyIntakeSummary = ({ patientId }) => {
    const [patientData, setPatientData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchPatientData = useCallback(async () => {
        if (!patientId) {
            setPatientData(null);
            return;
        };
        setLoading(true);
        setError(null);
        try {
            // Fetch patient summary data
            const patientRes = await axios.get(`/patients/${patientId}`);
            setPatientData(patientRes.data.data.patient);
        } catch (err) {
            console.error("Error fetching patient data for read-only summary:", err);
            setError(err.response?.data?.message || 'Failed to load patient summary data.');
            setPatientData(null);
        } finally {
            setLoading(false);
        }
    }, [patientId]);

    useEffect(() => {
        fetchPatientData();
    }, [fetchPatientData]);

    // Extract and flatten only the procedure arrays from the patient's permanent structured surgical history
    const permanentSurgicalHistoryList = patientData?.structuredSurgicalHistory
        ? Object.entries(patientData.structuredSurgicalHistory)
            .filter(([key, value]) => Array.isArray(value)) // Keep only entries where the value is an array
            .map(([key, value]) => value) // Get just the arrays
            .flat() // Flatten the array of arrays
            .filter(Boolean) // Remove any null/undefined/empty strings
        : [];

    return (
        <Spin spinning={loading}>
            {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
            {patientData && !loading && !error && (
                <Card size="small" title="Current Record Summary (Read-Only)" style={{ background: '#f9f9f9' }}>
                    {/* Allergies */}
                    <Descriptions title="Active Allergies" size="small" column={1} style={{ marginBottom: 10 }}>
                        <Descriptions.Item>
                            {patientData.allergies?.filter(a => a.status === 'Active').length > 0 ? (
                                <List size="small" dataSource={patientData.allergies.filter(a => a.status === 'Active')} renderItem={item => {
                                    const substanceText = (typeof item.substance === 'object' && item.substance?.name) ? item.substance.name : (item.substance || 'N/A');
                                    const reactionText = item.reaction || 'N/A';
                                    return <List.Item>{String(substanceText)} - {String(reactionText)}</List.Item>;
                                }} />
                            ) : <Text type="secondary">None Recorded</Text>}
                        </Descriptions.Item>
                    </Descriptions>
                    {/* Medications */}
                    <Descriptions title="Active Medications" size="small" column={1} style={{ marginBottom: 10 }}>
                        <Descriptions.Item>
                            {patientData.medications?.filter(m => m.status === 'Active').length > 0 ? (
                                <List size="small" dataSource={patientData.medications.filter(m => m.status === 'Active')} renderItem={item => {
                                    const medNameText = (typeof item.medicationName === 'object' && item.medicationName?.name) ? item.medicationName.name : (item.medicationName || 'N/A');
                                    const dosageText = item.dosage || '';
                                    const frequencyText = item.frequency || '';
                                    return <List.Item>{String(medNameText)} {String(dosageText)} {String(frequencyText)}</List.Item>;
                                }} />
                            ) : <Text type="secondary">None Recorded</Text>}
                        </Descriptions.Item>
                    </Descriptions>
                    {/* Active Problem List */}
                    <Descriptions title="Active Problem List" size="small" column={1} style={{ marginBottom: 10 }}>
                        <Descriptions.Item>
                            {patientData.problems?.filter(p => p.status === 'Active').length > 0 ? (
                                <List size="small" dataSource={patientData.problems.filter(p => p.status === 'Active')} renderItem={item => {
                                    const problemText = (typeof item.problemName === 'object' && item.problemName?.name) ? item.problemName.name : (item.problemName || 'N/A');
                                    const dateText = item.dateOfOnset ? `(${formatDate(item.dateOfOnset)})` : '';
                                    return <List.Item>{String(problemText)} {dateText}</List.Item>;
                                }} />
                            ) : <Text type="secondary">None Recorded</Text>}
                        </Descriptions.Item>
                    </Descriptions>
                    {/* Surgical History (from permanent structured record) */}
                    <Descriptions title="Surgical History" size="small" column={1} style={{ marginBottom: 10 }}>
                        <Descriptions.Item>
                            {permanentSurgicalHistoryList.length > 0 ? (
                                <List size="small" dataSource={permanentSurgicalHistoryList} renderItem={item => <List.Item>{item}</List.Item>} />
                            ) : <Text type="secondary">None Recorded (in permanent checklist)</Text>}
                        </Descriptions.Item>
                    </Descriptions>
                </Card>
            )}
            {!patientData && !loading && !error && (
                 <Alert message="Could not load patient summary data." type="warning" showIcon />
            )}
        </Spin>
    );
};

export default ReadOnlyIntakeSummary;
